/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import {
  useTable,
  usePagination,
  TableInstance,
  Column,
  UsePaginationInstanceProps,
  UsePaginationState,
  TableOptions,
  ColumnInstance,
} from 'react-table';
import Pagination from '../Pagination';
import {
  Box,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Text,
} from '@chakra-ui/react';
import SearchIcon from '../../../assets/svg/search.svg?react';
import FilterPopover from './FilterPopOver';
import { RoleTitle } from '@/services/userManagement/types';
import { ArrowUpDownIcon, ChevronUpIcon, ChevronDownIcon } from '@chakra-ui/icons';

declare module 'react-table' {
  interface ColumnInterface {
    disableSorting?: boolean;
  }
}

interface DatatableProps<T extends object> {
  data: T[];
  columns: Column<T>[];
  headerButton?: React.ReactNode;
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  onSearch?: (searchTerm: string) => void;
  onPageChange: (page: number) => void;
  filters?: {
    start_date?: string;
    end_date?: string;
    from_date?: string;
    to_date?: string;
    category?: any;
    subcategory?: any;
    assign_title?: string;
    user_status?: boolean | null;
  };
  setFilters?: React.Dispatch<
    React.SetStateAction<{
      start_date?: string;
      end_date?: string;
      from_date?: string;
      to_date?: string;
      category?: any;
      subcategory?: any;
      assign_title?: string;
      user_status?: boolean | null;
    }>
  >;
  applyFilters?: () => void;
  resetFilters?: () => void;
  isLoading?: boolean;
  isError?: boolean;
  sticky?: boolean;
  hideFilters?: boolean;
  headerText?: string;
  hideCategoryFilter?: boolean;
  hideSubcategoryFilter?: boolean;
  rolesFilter?: RoleTitle[];
  dateFilterType?: 'start_end' | 'from_to';
  restrictDateRange?: boolean;
  sendCategoryIds?: boolean;
  sorting?: { id: string; desc: boolean }[];
  onSortingChange?: (sorting: { id: string; desc: boolean }[]) => void;
  manualSorting?: boolean;
}

const Datatable = <T extends object>({
  data,
  isLoading,
  isError,
  columns,
  pagination,
  onSearch,
  onPageChange,
  filters,
  setFilters,
  applyFilters,
  resetFilters,
  headerButton,
  sticky,
  hideFilters,
  headerText,
  hideCategoryFilter,
  rolesFilter,
  dateFilterType = 'start_end',
  restrictDateRange,
  sorting = [],
  onSortingChange,
  manualSorting = false,
  sendCategoryIds = false,
  hideSubcategoryFilter = false,
}: DatatableProps<T>) => {
  const [searchTerm, setSearchTerm] = useState<string>('');

  const hasSorting = !!onSortingChange;

  const handleHeaderClick = (column: ColumnInstance<T>) => {
    if (!hasSorting || !manualSorting || column.disableSorting) return;

    const current = sorting.find((s) => s.id === column.id);
    let newSorting: { id: string; desc: boolean }[] = [];

    if (!current) {
      newSorting = [{ id: column.id, desc: false }];
    } else if (!current.desc) {
      newSorting = [{ id: column.id, desc: true }];
    }

    onSortingChange(newSorting);
  };

  const getSortIcon = (columnId: string) => {
    if (!manualSorting) return null;
    const sort = sorting.find((s) => s.id === columnId);
    if (!sort) return <ArrowUpDownIcon boxSize="12px" color="#4A5769" />;
    return sort.desc ? (
      <ChevronDownIcon boxSize="14px" color="#4A5769" />
    ) : (
      <ChevronUpIcon boxSize="14px" color="#4A5769" />
    );
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    gotoPage,
    previousPage,
    nextPage,
    state: { pageIndex },
  } = useTable<T>(
    {
      columns,
      data,
      manualPagination: true,
      pageCount: pagination.totalPages,
      initialState: {
        pageIndex: pagination?.page ? pagination.page - 1 : 0,
        pageSize: pagination?.pageSize || 10,
      } as Partial<UsePaginationState<T>>,
    } as TableOptions<T>,
    usePagination,
  ) as TableInstance<T> & UsePaginationInstanceProps<T> & { state: UsePaginationState<T> };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value;
    setSearchTerm(searchValue);
    if (onSearch) {
      onSearch(searchValue);
    }
  };

  const renderCellSafely = (cell: any) => {
    const value = cell.render('Cell');
    if (typeof value === 'bigint') return value.toString();
    return value;
  };

  return (
    <Box
      bg={'background.white'}
      sx={{
        border: '1px solid var(--Boarder-Default, #D0D5DD)',
        boxShadow: '0px 1px 2px 0px #1018280D',
        borderRadius: 'xl',
      }}
      py={hideFilters ? 0 : 4}
    >
      {headerText && (
        <Flex
          bg={'white'}
          alignItems={{ base: 'start', lg: 'center' }}
          flexDirection={{ base: 'column', lg: 'row' }}
          px={6}
          py={4}
          gap={2}
          borderRadius={'xl'}
        >
          <Text
            fontSize={'md'}
            color={'text.primary'}
            fontFamily={'primary'}
            fontWeight={'semibold'}
          >
            {headerText}
          </Text>
        </Flex>
      )}

      {hideFilters !== true && (
        <Flex
          bg={'white'}
          justifyContent="space-between"
          alignItems={{ base: 'start', lg: 'center' }}
          flexDirection={{ base: 'column', lg: 'row' }}
          mb={4}
          px={6}
          gap={2}
        >
          <InputGroup width="300px">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.500" />
            </InputLeftElement>
            <Input
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search..."
              borderColor="border.default"
              focusBorderColor="brand.primary.DEFAULT"
              rounded="xl"
            />
          </InputGroup>
          <HStack gap={2}>
            <FilterPopover
              filters={filters}
              setFilters={setFilters}
              applyFilters={applyFilters}
              resetFilters={resetFilters}
              setPage={() => gotoPage(1)}
              hideCategoryFilter={hideCategoryFilter}
              hideSubcategoryFilter={hideSubcategoryFilter}
              rolesFilter={rolesFilter}
              dateFilterType={dateFilterType}
              restrictDateRange={restrictDateRange}
              sendCategoryIds={sendCategoryIds}
            />
            {headerButton && headerButton}
          </HStack>
        </Flex>
      )}

      <Box
        overflowX={'auto'}
        borderBottom={'1px'}
        borderColor={'border.default'}
        sx={{
          '&::-webkit-scrollbar': { height: '4px' },
          '&::-webkit-scrollbar-track': {
            background: 'background.secondary',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': { background: 'brand.primary.300', borderRadius: '10px' },
          '&::-webkit-scrollbar-thumb:hover': { background: 'brand.primary.400' },
        }}
      >
        <table
          {...getTableProps()}
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontFamily: `'Josefin Sans', sans-serif`,
            color: '#090F1A',
            fontSize: '14px',
          }}
          className={sticky ? 'datatable-sticky' : ''}
        >
          <thead style={{ backgroundColor: '#f4f4f4' }}>
            {headerGroups.map((headerGroup) => {
              const { key: headerGroupKey, ...headerGroupProps } =
                headerGroup.getHeaderGroupProps();

              return (
                <tr key={headerGroupKey} {...headerGroupProps}>
                  {headerGroup.headers.map((column, index) => {
                    const isSortable = hasSorting && manualSorting && !column.disableSorting;
                    const { key, ...headerProps } = column.getHeaderProps();

                    return (
                      <th
                        key={key}
                        {...headerProps}
                        onClick={() => isSortable && handleHeaderClick(column)}
                        style={{
                          borderTop: hideFilters !== true ? '1px solid #D0D5DD66' : 'none',
                          paddingLeft: index === 0 ? '40px' : '24px',
                          paddingRight: '24px',
                          paddingTop: '12px',
                          paddingBottom: '12px',
                          backgroundColor: '#f2f4f7',
                          textAlign: 'left',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: '#4A5769',
                          cursor: isSortable ? 'pointer' : 'default',
                          userSelect: 'none',
                          height: '48px',
                          verticalAlign: 'middle',
                        }}
                      >
                        <Flex alignItems="center" gap={1}>
                          <Text lineHeight={1}>{column.render('Header') as string}</Text>
                          {isSortable && getSortIcon(column.id)}
                        </Flex>
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} style={{ textAlign: 'center', padding: '24px' }}>
                  <Flex justify="center" align="center" h="100px">
                    <Spinner size="lg" color="brand.primary.DEFAULT" />
                  </Flex>
                </td>
              </tr>
            ) : isError ? (
              <tr>
                <td colSpan={columns.length} style={{ textAlign: 'center', padding: '24px' }}>
                  <Text color={'text.disable'}>Something went wrong while fetching data.</Text>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{
                    textAlign: 'center',
                    padding: '16px',
                    color: '#6B7280',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  No data available
                </td>
              </tr>
            ) : (
              page.map((row, index) => {
                prepareRow(row);
                const isLastRow = index === page.length - 1;
                const { key: rowKey, ...rowProps } = row.getRowProps();

                return (
                  <tr
                    key={rowKey}
                    {...rowProps}
                    style={{
                      borderBottom:
                        isLastRow && pagination.totalPages > 1 ? '1px solid #D0D5DD66' : 'none',
                    }}
                  >
                    {row.cells.map((cell, cellIndex) => {
                      const { key: cellKey, ...cellProps } = cell.getCellProps();

                      return (
                        <td
                          key={cellKey}
                          {...cellProps}
                          style={{
                            borderTop: '1px solid #D0D5DD66',
                            paddingLeft: cellIndex === 0 ? '40px' : '24px',
                            paddingRight: '24px',
                            paddingTop: '15px',
                            paddingBottom: '15px',
                            color: '#090F1A',
                            fontSize: '14px',
                            fontWeight: '400',
                          }}
                        >
                          {renderCellSafely(cell)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </Box>

      <HStack width={'100%'} px={6} py={4}>
        {pagination && pagination.totalPages > 1 && (
          <Pagination
            currentPage={pageIndex + 1}
            pageCount={pagination.totalPages}
            canPreviousPage={canPreviousPage}
            canNextPage={pageIndex < pagination.totalPages - 1}
            previousPage={previousPage}
            nextPage={nextPage}
            pageButtons={Array.from({ length: pagination.totalPages }, (_, index) => index + 1)}
            setPage={(page: number) => {
              gotoPage(page - 1);
              if (onPageChange) {
                onPageChange(page);
              }
            }}
          />
        )}
      </HStack>
    </Box>
  );
};

export default Datatable;
